import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { Suspense } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import {
  InGameCurrency,
  Rarity,
} from '@sorare/core/src/__generated__/globalTypes';
import { Amount } from '@sorare/core/src/atoms/Amount';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandL, BrandXS } from '@sorare/core/src/atoms/typography';
import { ProbabilisticBundleReward } from '@sorare/core/src/components/3d/ProbabilisticBundleReward';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { glossary, wheel } from '@sorare/core/src/lib/glossary';
import { qualityNames } from '@sorare/core/src/lib/players';
import { lazy } from '@sorare/core/src/lib/retry';
import {
  inGameCurrencyToRarity,
  scarcityMessages,
} from '@sorare/core/src/lib/scarcity';

import { Shape } from '../Shape';
import type {
  Description_probabilisticBundleSlotConfig,
  Reward_probabilisticBundleSlotConfig,
  Reward_probabilisticBundleSlotItemInterface,
} from './__generated__/index.graphql';

const Scene = lazy(
  async () => import('@sorare/trois/src/components/Scene'),
  'Scene'
);

const Wrapper = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  align-content: center;
  justify-content: center;
  isolation: isolate;
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-color: var(--c-nl-700);
    z-index: -1;
  }
`;

const DescriptionContainer = styled.div`
  position: absolute;
  bottom: var(--double-unit);
  left: var(--double-unit);
  right: var(--double-unit);
  text-align: center;
`;

type Props = {
  item: Reward_probabilisticBundleSlotItemInterface;
  config: Reward_probabilisticBundleSlotConfig;
  rarity: Rarity;
  onClose: () => void;
};

const Description = ({
  config,
}: {
  config: Description_probabilisticBundleSlotConfig;
}) => {
  const { toMonetaryAmount } = useMonetaryAmount();

  if (config.deliverableItems?.length) {
    return (
      <BrandL>
        <FormattedMessage
          id="wheel.slot.deliverableItem"
          defaultMessage="JERSEY"
        />
      </BrandL>
    );
  }

  // card
  if (config.cards?.length) {
    const card = config.cards[0];
    return <BrandL>{!!card.quality && qualityNames[card.quality]}</BrandL>;
  }

  // card shard
  if (config.cardShards?.length) {
    const cardShard = config.cardShards[0];
    return (
      <BrandL>
        {cardShard.quantity}{' '}
        <FormattedMessage {...scarcityMessages[cardShard.rarity]} />{' '}
        <FormattedMessage {...glossary.essence} />
      </BrandL>
    );
  }

  // conversion credit
  if (config.conversionCredit) {
    return (
      <>
        <BrandL>
          <Amount
            monetaryAmount={toMonetaryAmount(
              config.conversionCredit.maxDiscount
            )}
          />
        </BrandL>
        <BrandXS color="var(--c-nd-500)">
          <FormattedNumber
            value={config.conversionCredit.percentageDiscount}
            maximumFractionDigits={0}
            style="percent"
          />
        </BrandXS>
      </>
    );
  }

  if (config.inGameCurrencies?.length) {
    const igc = config.inGameCurrencies.filter(
      x => x.currency !== InGameCurrency.GOLDEN_WHEEL_TICKET
    )[0];
    // XP
    if (
      igc?.currency === InGameCurrency.LIMITED_XP ||
      igc?.currency === InGameCurrency.COMMON_XP
    ) {
      return (
        <BrandL>
          {igc.amount}{' '}
          {inGameCurrencyToRarity[igc.currency] !== null && (
            <FormattedMessage
              {...scarcityMessages[inGameCurrencyToRarity[igc.currency]!]}
            />
          )}{' '}
          XP
        </BrandL>
      );
    }
    if (igc?.currency === InGameCurrency.COMPETITION_CRAFT_CLUE) {
      return (
        <BrandL>
          {igc.amount} <FormattedMessage {...wheel.competitionClue} />
        </BrandL>
      );
    }
    if (igc?.currency === InGameCurrency.DIVISION_CRAFT_CLUE) {
      return (
        <BrandL>
          {igc.amount} <FormattedMessage {...wheel.divisionClue} />
        </BrandL>
      );
    }
    if (igc?.currency === InGameCurrency.POSITION_CRAFT_CLUE) {
      return (
        <BrandL>
          {igc.amount} <FormattedMessage {...wheel.positionClue} />
        </BrandL>
      );
    }
    if (igc?.currency === InGameCurrency.COUNTRY_CRAFT_CLUE) {
      return (
        <BrandL>
          {igc.amount} <FormattedMessage {...wheel.countryClue} />
        </BrandL>
      );
    }
    if (igc?.currency === InGameCurrency.FIFTY_FIFTY_CRAFT_CLUE) {
      return (
        <BrandL>
          {igc.amount} <FormattedMessage {...wheel.bestFiveClue} />
        </BrandL>
      );
    }
    if (igc?.currency === InGameCurrency.SPECIAL_EVENT_CRAFT_CLUE) {
      return (
        <BrandL>
          {igc.amount} <FormattedMessage {...wheel.potionClue} />
        </BrandL>
      );
    }
    // respin
    if (igc?.currency === InGameCurrency.WHEEL_TICKET) {
      return (
        <BrandL>
          <FormattedMessage {...wheel.spinAgain} />
        </BrandL>
      );
    }
  }
  return (
    <BrandL>
      <FormattedMessage {...wheel.nothing} />
    </BrandL>
  );
};

Description.fragments = {
  ProbabilisticBundleSlotConfig: gql`
    fragment Description_probabilisticBundleSlotConfig on ProbabilisticBundleSlotConfig {
      id
      deliverableItems {
        deliverableItem {
          slug
          name
        }
      }
      cards {
        quality
      }
      rewardShopItems {
        quantity
        shopItem {
          id
          name
        }
      }
      conversionCredit {
        id
        maxDiscount {
          ...useMonetaryAmount_monetaryAmount
        }
        percentageDiscount
      }
      inGameCurrencies {
        id
        amount
        currency
      }
      cardShards {
        id
        quantity
        rarity
      }
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<Description_probabilisticBundleSlotConfig>,
};

export const Reward = ({ item, config, rarity, onClose }: Props) => {
  return (
    <Wrapper onClick={onClose}>
      <Vertical center gap={3}>
        <BrandL className="text-center">
          <FormattedMessage id="Reward.youWon" defaultMessage="You've won:" />
        </BrandL>
        <Shape
          config={config}
          style={{
            viewTransitionName: 'wheel-reward',
          }}
          rarity={rarity}
        >
          <Vertical
            style={{
              height: '400px',
              position: 'relative',
              minWidth: 'min(300px, 100vw)',
            }}
            center
          >
            <Suspense fallback={null}>
              <Scene fallback={null}>
                <ProbabilisticBundleReward item={item} />
              </Scene>
            </Suspense>
            <DescriptionContainer>
              <Description config={config} />
            </DescriptionContainer>
          </Vertical>
        </Shape>
        <Button color="primary" size="large" onClick={onClose}>
          <FormattedMessage {...glossary.ok} />
        </Button>
      </Vertical>
    </Wrapper>
  );
};

Reward.fragments = {
  probabilisticBundleSlotItemInterface: gql`
    fragment Reward_probabilisticBundleSlotItemInterface on ProbabilisticBundleSlotItemInterface {
      id
      ...ProbabilisticBundleReward_probabilisticBundleSlotItemInterface
    }
    ${ProbabilisticBundleReward.fragments.probabilisticBundleSlotItemInterface}
  ` as TypedDocumentNode<Reward_probabilisticBundleSlotItemInterface>,
  probabilisticBundleSlotConfig: gql`
    fragment Reward_probabilisticBundleSlotConfig on ProbabilisticBundleSlotConfig {
      id
      ...Shape_probabilisticBundleSlotConfig
      ...Description_probabilisticBundleSlotConfig
    }
    ${Shape.fragments.ProbabilisticBundleSlotConfig}
    ${Description.fragments.ProbabilisticBundleSlotConfig}
  ` as TypedDocumentNode<Reward_probabilisticBundleSlotConfig>,
};
